import { useEffect } from 'react';
import { useAngularContext } from 'AngularContext';
import { useErrorLogService } from 'ErrorLogging';
import { type StreamForStreamDashboard } from 'Lessons';
import { type AnyObject } from '@Types';
import { getStreamLogInfo, isLessonComplete, isStreamComplete } from '../selectors';

/*
    Finishes the clickedToNavigateToStreamDashboard timer and emits renderedStreamDashboard (PerformanceTest
    waits for it). Reports a stream whose complete flag disagrees with its lessons.
*/

// Module level, so the report fires once per locale pack per session.
const reportedDisagreements = new Set<string>();

// Mirrors Stream#allLessonsComplete, including its "no lessons is not complete" rule.
function allLessonsComplete(stream: StreamForStreamDashboard) {
    if (!stream.lessons.length) return false;
    return stream.lessons.every(isLessonComplete);
}

function getDisagreementMessage(stream: StreamForStreamDashboard) {
    const lessonsComplete = allLessonsComplete(stream);
    const streamComplete = isStreamComplete(stream);

    if (lessonsComplete && !streamComplete) {
        return 'Stream rendered in the UI is not complete even though all the lessons are';
    }
    if (streamComplete && !lessonsComplete) {
        return 'Stream rendered in the UI is complete even though not all the lessons are';
    }
    return null;
}

export function useStreamDashboardRendered(stream: StreamForStreamDashboard | null) {
    const $injector = useAngularContext();
    const ErrorLogService = useErrorLogService();

    useEffect(() => {
        if (!stream) return;

        const timerSingleton = $injector.get<AnyObject<(...args: unknown[]) => void>>('timerSingleton');
        timerSingleton.finishTimer(
            `clickedToNavigateToStreamDashboard-${stream.id}`,
            'rendered_stream_dashboard',
            getStreamLogInfo(stream),
        );

        const $rootScope = $injector.get<{ $emit: (name: string) => void }>('$rootScope');
        $rootScope.$emit('renderedStreamDashboard');
    }, [stream, $injector]);

    useEffect(() => {
        if (!stream || reportedDisagreements.has(stream.localePackId)) return;

        const message = getDisagreementMessage(stream);
        if (!message) return;

        reportedDisagreements.add(stream.localePackId);

        // notifyInProd throws in development. In a passive effect, that throw unmounts the page into the error boundary.
        try {
            ErrorLogService.notifyInProd(message, undefined, {
                streamLocalePackId: stream.localePackId,
                streamProgress: stream.lessonStreamsProgress && {
                    id: stream.lessonStreamsProgress.id,
                    updated_at: stream.lessonStreamsProgress.updatedAt,
                },
                lessonProgress: stream.lessons.map(lesson => ({
                    id: lesson.lessonProgress?.id,
                    updated_at: lesson.lessonProgress?.updatedAt,
                })),
            });
        } catch {
            // See above.
        }
    }, [stream, ErrorLogService]);
}
